import { useCallback } from 'react';
import useUserDataStore from '../store/userDataStore';
import { useToken } from '../components/auth/useToken';

// JWT decode utility
const safeJwtDecode = (token) => {
  if (
    !token ||
    typeof token !== 'string' ||
    token === 'null' ||
    token === 'undefined'
  ) {
    return null;
  }
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    const encodedPayload = parts[1];
    if (!encodedPayload) {
      return null;
    }
    return JSON.parse(atob(encodedPayload));
  } catch (error) {
    return null;
  }
};

// Main role hook
export const useRole = () => {
  const [token] = useToken();
  const { userData } = useUserDataStore();

  const getUserRole = useCallback(() => {
    // First try to get role from userData store
    if (userData?.role && userData.role !== 'user') {
      // console.log('useRole: Found role in userData store:', userData.role);
      return userData.role;
    }

    // Fallback to JWT token
    if (token) {
      const decodedToken = safeJwtDecode(token);
      const tokenRole = decodedToken?.role || 'user';
      // console.log(
      //   'useRole: Found role in JWT token:',
      //   tokenRole,
      //   'decodedToken:',
      //   decodedToken
      // );
      return tokenRole;
    }

    // console.log('useRole: No token found, defaulting to user role');
    return 'user';
  }, [token, userData]);

  const role = getUserRole();

  return {
    role,
    isUser: role === 'user',
    isModerator: role === 'moderator',
    isAdmin: role === 'admin',
    isSuperAdmin: role === 'super_admin',
    hasModeratorAccess:
      role === 'moderator' || role === 'admin' || role === 'super_admin',
    hasAdminAccess: role === 'admin' || role === 'super_admin',
    hasSuperAdminAccess: role === 'super_admin',
  };
};

// Individual role checking hooks
export const useIsAdmin = () => {
  const { hasAdminAccess } = useRole();
  return hasAdminAccess;
};

export const useIsModerator = () => {
  const { hasModeratorAccess } = useRole();
  return hasModeratorAccess;
};

export const useIsSuperAdmin = () => {
  const { hasSuperAdminAccess } = useRole();
  return hasSuperAdminAccess;
};

// Permission checking utilities
export const checkPermission = (userRole, requiredRole) => {
  const roleHierarchy = {
    user: 0,
    moderator: 1,
    admin: 2,
    super_admin: 3,
  };

  const userLevel = roleHierarchy[userRole] || 0;
  const requiredLevel = roleHierarchy[requiredRole] || 0;

  return userLevel >= requiredLevel;
};
